import { zodResolver } from '@hookform/resolvers/zod';
import { useHeaderHeight } from '@react-navigation/elements';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'expo-router';
import { useForm } from 'react-hook-form';
import { KeyboardAvoidingView, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import { useCustomer } from '@/api/customers/use-customer';
import { useCustomerUpdatePassword } from '@/api/customers/use-customer-update-password';
import { useCustomerEmailVerifyRequest } from '@/api/customers/use-email-verify';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop } from '@/core';
import { GREY_40 } from '@/style/colors';
import { Button, showErrorMessage, Text } from '@/ui';
import { ControlledInput } from '@/ui/input/index';
import { Loader } from '@/ui/loader';

type PasswordFormProps = {
  currentPassword: string;
  newPassword: string;
};

const passwordFormSchema = z
  .object({
    currentPassword: z.string().min(8, 'Minimum 8 characters'),
    newPassword: z.string().min(8, 'Minimum 8 characters'),
    confirmNewPassword: z.string().min(8, 'Minimum 8 characters'),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords must match.',
    path: ['confirmNewPassword'],
  });

export type PasswordFormType = z.infer<typeof passwordFormSchema>;

export default function ProfilePassword() {
  const { styles, theme } = useStyles(stylesheet);
  const headerHeight = useHeaderHeight();
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const router = useRouter();

  const { mutate: updateCustomerPassword, isPending: isUpdatingPassword } =
    useCustomerUpdatePassword();

  const {
    mutate: sendEmailVerifyRequest,
    isPending: isSendingEmailVerificationRequest,
  } = useCustomerEmailVerifyRequest();

  const { data: customer } = useCustomer();

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    formState: { isValid },
    getValues,
    reset,
  } = useForm<PasswordFormType>({
    resolver: zodResolver(passwordFormSchema),
  });

  const handleContinue = (data: PasswordFormProps) => {
    updateCustomerPassword(
      {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          Toast.show({
            text1: 'Password updated successfully',
            type: 'success',
          });

          reset();
        },
        onError: (e) => {
          showErrorMessage(e.message);
        },
      }
    );
  };

  const handleForgotPassword = () => {
    if (pathname.includes('partner')) {
      router.push('/partner/more/forgot-password');
    } else {
      router.push('/account/forgot-password');
    }
  };

  if (isUpdatingPassword) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{
        flexGrow: 1,
        backgroundColor: theme.colors.background_1,
        alignItems: 'center',
      }}
    >
      <View style={styles.container}>
        {isDesktop() ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                if (pathname.includes('/partner')) {
                  router.navigate('/partner/more/profile');
                  return;
                }

                router.navigate('/account/profile');
              }}
            />

            <Text type="largeTitle">Password</Text>
          </View>
        ) : null}

        <View style={styles.contentContainer}>
          <View style={{ gap: 24 }}>
            <Text
              tx="profilePassword.instructions"
              type="bodyRegular"
              style={{
                color: GREY_40,
              }}
            />

            <ControlledInput
              control={control}
              name="currentPassword"
              label="Current password"
              secureTextEntry
              onBlur={() => {
                trigger('currentPassword');
              }}
              onChangeText={(text) => {
                setValue('currentPassword', text);
                trigger('currentPassword');
              }}
            />

            <ControlledInput
              control={control}
              name="newPassword"
              label="New password"
              secureTextEntry
              onBlur={() => {
                trigger('newPassword');
              }}
              onChangeText={(text) => {
                setValue('newPassword', text);
                trigger('newPassword');
              }}
            />

            <ControlledInput
              control={control}
              name="confirmNewPassword"
              label="Re-enter password"
              secureTextEntry
              onBlur={() => {
                trigger('confirmNewPassword');
              }}
              onChangeText={(text) => {
                setValue('confirmNewPassword', text);
                trigger('confirmNewPassword');
              }}
            />
          </View>

          <View>
            <Button
              type="primary"
              tx="profilePassword.update"
              onPress={handleSubmit(handleContinue)}
              disabled={!isValid}
            />

            <Button
              tx="profilePassword.forgotPassword"
              type="ghost"
              onPress={handleForgotPassword}
              style={{ alignSelf: 'center' }}
            />
          </View>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    gap: 16,
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 600,
    },
  },
  contentContainer: {
    flexGrow: 1,
    gap: 24,
    justifyContent: {
      xs: 'space-between',
      md: 'flex-start',
    },
  },
}));
